import { useState } from 'react';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import DeleteResourceControl from '../shared/DeleteResourceControl';
import { useDependents } from '../../hooks/useDependents';
import { useFlag } from '../../hooks/useFeatureFlags';
import { useDeleteModelConfig, useModelConfigs } from '../../hooks/useModelConfigs';
import { formatTokenCount } from '../../utils/formatUtils';
import { modelLabel } from '../modelconfigs/modelConfigRules';
import { ModelConfigDialog } from './ModelConfigDialog';
import type { Model, ModelConfig } from '../../types';

/**
 * The Model Configs that run on one Model, inline under it (task 1167).
 *
 * Used twice, the same component both times: under an expanded Model row on
 * the Models page, and on the Model page in place of its old "N Model Configs"
 * link-out. Each row shows only the config's own choices — context budget,
 * output limit, temperature, prompt caching — and how many agents use it.
 *
 * Edit opens the config dialog. Delete is offered only when no agent uses the
 * config, read from the config's `/dependents`. Both pages that mount this are
 * platform pages, so platform staff holding `innovate.fe.model-catalog.manage`
 * (mirroring the service's `innovate.be.model-catalog.manage` gate) read it with
 * `scope=platform` and count agents in every account (task 1181); without that
 * flag the read stays account-scoped and the column says so. Delete goes through
 * the one delete control; "Add config on this Model" closes the list. Writes ride the Model
 * Config flags: `innovate.fe.model-configs.create` for add and edit,
 * `innovate.fe.model-configs.delete` for delete.
 */

/** Configs one Model can carry in the inline list. A Model has a handful. */
const INLINE_CONFIG_LIMIT = 100;

interface ModelConfigsInlineProps {
  model: Model;
  /** Rendered on the Models page inside a table cell; on the Model page as a card. */
  variant?: 'row' | 'card';
}

function cachingText(config: ModelConfig, model: Model): string {
  if (model.promptCaching === 'automatic') return 'cached automatically';
  if (model.promptCaching === 'none') return 'no caching';
  return config.promptCaching ? 'cached' : 'cache off';
}

/** `'platform'` when the caller may read agents across every account, else account-scoped. */
function useDependentsScope(): 'platform' | undefined {
  return useFlag('innovate.fe.model-catalog.manage') ? 'platform' : undefined;
}

function AgentCount({ configId }: { configId: string }) {
  const scope = useDependentsScope();
  const { data, isLoading, isError } = useDependents('model-configs', configId, true, scope);
  if (isLoading) return <span className="text-gray-400">…</span>;
  if (isError || !data) return <span className="text-gray-400">—</span>;
  const count = data.dependents.agents.length;
  return (
    <span data-testid={`model-config-agents-${configId}`}>
      {count === 0 ? 'no agents' : `${count} agent${count === 1 ? '' : 's'}`}
    </span>
  );
}

function ConfigRow({
  config,
  model,
  canEdit,
  onEdit,
}: {
  config: ModelConfig;
  model: Model;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const remove = useDeleteModelConfig();
  const dependents = useDependents('model-configs', config.id, true, useDependentsScope());
  const unused = dependents.data ? dependents.data.dependents.agents.length === 0 : false;
  const name = config.name ?? model.modelId;

  return (
    <tr data-testid={`model-config-row-${config.id}`} className="text-sm text-gray-700 dark:text-gray-300">
      <td className="py-2 pr-4">
        <span className="font-mono font-medium text-gray-900 dark:text-white">{name}</span>
        {config.isPlatformDefault && (
          <span
            data-testid={`model-config-platform-default-${config.id}`}
            className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
          >
            Platform default
          </span>
        )}
      </td>
      <td className="py-2 pr-4">
        {config.contextBudgetTokens ? formatTokenCount(config.contextBudgetTokens) : 'full window'}
      </td>
      <td className="py-2 pr-4">{Number(config.maxOutputTokens).toLocaleString('en-US')}</td>
      <td className="py-2 pr-4">
        temp {config.temperature}
        {model.supportsTemperature === false && <span className="text-gray-400"> (not sent)</span>}
      </td>
      <td className="py-2 pr-4">{cachingText(config, model)}</td>
      <td className="py-2 pr-4">
        <AgentCount configId={config.id} />
      </td>
      <td className="whitespace-nowrap py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              data-testid={`model-config-edit-${config.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
            >
              <PencilIcon className="h-3.5 w-3.5" />
              Edit
            </button>
          )}
          {unused && (
            <DeleteResourceControl
              flag="innovate.fe.model-configs.delete"
              entity="model-configs"
              entityLabel="Model config"
              name={name}
              id={config.id}
              testId={`model-config-delete-${config.id}`}
              body={<p>No agent uses this config. Agents choose a config, so nothing else changes.</p>}
              onDelete={() => remove.mutateAsync(config.id)}
            />
          )}
        </div>
      </td>
    </tr>
  );
}

export function ModelConfigsInline({ model, variant = 'row' }: ModelConfigsInlineProps) {
  const canEdit = useFlag('innovate.fe.model-configs.create');
  const platformWide = useDependentsScope() === 'platform';
  // Every config on the Model, its provider switched off or not (task 1226)
  const { data, isLoading, error } = useModelConfigs(1, INLINE_CONFIG_LIMIT, { modelId: model.id, includeDisabledProviders: true });
  const [dialog, setDialog] = useState<{ config: ModelConfig | null } | null>(null);
  const configs = [...(data?.data ?? [])].sort(
    (a, b) => Number(!!b.isPlatformDefault) - Number(!!a.isPlatformDefault) || (a.name ?? '').localeCompare(b.name ?? ''),
  );
  const label = modelLabel(model);
  const canAdd = canEdit && model.status !== 'retired';

  return (
    <div
      data-testid={`model-configs-inline-${model.id}`}
      className={variant === 'card' ? 'rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800' : 'pl-6'}
    >
      {variant === 'card' && (
        <h2 className="mb-3 text-lg font-medium text-gray-900 dark:text-white">Model Configs on this Model</h2>
      )}
      {isLoading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading configs…</p>
      ) : error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Could not load the configs on this Model.</p>
      ) : configs.length === 0 ? (
        <p data-testid={`model-configs-empty-${model.id}`} className="text-sm text-gray-500 dark:text-gray-400">
          No Model Configs run on {label} yet. Agents choose a config, not a Model.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-4">Name</th>
                <th className="py-1 pr-4">Context budget</th>
                <th className="py-1 pr-4">Output limit</th>
                <th className="py-1 pr-4">Temperature</th>
                <th className="py-1 pr-4">Prompt caching</th>
                <th
                  className="py-1 pr-4"
                  title={
                    platformWide
                      ? 'Agents in every account that use the config.'
                      : "Agents in your account that use the config. Other accounts' agents are not counted."
                  }
                >
                  {platformWide ? 'Agents' : 'Agents (this account)'}
                </th>
                <th className="py-1">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {configs.map((config) => (
                <ConfigRow
                  key={config.id}
                  config={config}
                  model={model}
                  canEdit={canEdit}
                  onEdit={() => setDialog({ config })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canAdd && (
        <button
          type="button"
          onClick={() => setDialog({ config: null })}
          data-testid={`model-config-add-inline-${model.id}`}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          <PlusIcon className="h-4 w-4" />
          Add config on {label}
        </button>
      )}

      <ModelConfigDialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        model={model}
        config={dialog?.config ?? null}
      />
    </div>
  );
}

export default ModelConfigsInline;
